import {
  HelperResult,
  JobSeeker,
  JobSeekerProofFile,
  JobSeekerProofFileMutationBody,
} from '@openclassrooms/sdk-web';
import getCRUDHooks from '../../utils/services/getCRUDHooks';
import { clientApi } from '../../configuration';

interface JobSeekersParams {
  userId: number;
}

export interface JobSeekerParams extends JobSeekersParams {
  jobAgency: 'pole-emploi';
}

interface JobSeekerMutationParams extends JobSeekerParams {
  context: string;
}

const {
  useFind: useFindJobSeekers,
  useUpdate: useUpdateJobSeeker,
  useDelete: useDeleteJobSeeker,
} = getCRUDHooks({
  entityName: 'jobSeekers',

  find: ({ userId }: JobSeekersParams): HelperResult<JobSeeker[]> =>
    clientApi.getOCClient().helpers.user.users.id(userId).jobSeekers.find(),

  update: (
    values: Pick<JobSeeker, 'jobSeekerId'>,
    { userId, jobAgency, context }: JobSeekerMutationParams
  ): HelperResult<JobSeeker> =>
    clientApi
      .getOCClient()
      .helpers.user.users.id(userId)
      .jobSeekers.update(jobAgency, values, false)
      .setHeader('X-Request-Context', context)
      .execute(),

  remove: ({ userId, jobAgency }: JobSeekerParams): HelperResult<void> =>
    clientApi.getOCClient().helpers.user.users.id(userId).jobSeekers.delete(jobAgency),
});

const {
  useFind: useFindJobSeekerProofFiles,
  useCreate: useCreateJobSeekerProofFile,
  useUpdate: useUpdateJobSeekerProofFile,
  useDelete: useDeleteJobSeekerProofFile,
} = getCRUDHooks({
  entityName: 'jobSeekersProofFiles',

  find: ({ userId, jobAgency }: JobSeekerParams): HelperResult<JobSeekerProofFile[]> =>
    clientApi
      .getOCClient()
      .helpers.user.users.id(userId)
      .jobSeekers.id(jobAgency)
      .proofFiles.find(),

  create: (
    values: JobSeekerProofFileMutationBody,
    { userId, jobAgency, context }: JobSeekerMutationParams
  ): HelperResult<JobSeekerProofFile> =>
    clientApi
      .getOCClient()
      .helpers.user.users.id(userId)
      .jobSeekers.id(jobAgency)
      .proofFiles.create(values, false)
      .setHeader('X-Request-Context', context)
      .execute(),

  update: (
    { id, ...values }: JobSeekerProofFileMutationBody & Pick<JobSeekerProofFile, 'id'>,
    { context }: { context: string }
  ): HelperResult<JobSeekerProofFile> =>
    clientApi
      .getOCClient()
      .helpers.jobSeekerProofFiles.jobSeekerProofFiles.update(id, values, false)
      .setHeader('X-Request-Context', context)
      .execute(),

  remove: ({ id }: Pick<JobSeekerProofFile, 'id'>): HelperResult<void> =>
    clientApi.getOCClient().helpers.jobSeekerProofFiles.jobSeekerProofFiles.delete(id),
});

export {
  useFindJobSeekers,
  useUpdateJobSeeker,
  useDeleteJobSeeker,
  useFindJobSeekerProofFiles,
  useCreateJobSeekerProofFile,
  useUpdateJobSeekerProofFile,
  useDeleteJobSeekerProofFile,
};
